import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  Spin,
  Form,
  Button,
  Tooltip,
  Typography,
  Table,
  Breadcrumb,
  Result,
} from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import get from "lodash/get";
import dayjs from "dayjs";
import { getOperatorSummaryColumns } from "../../constants/tableColumns/summaryReportsColumns/getOperatorSummaryColumns";
import { fetchCompanies } from "../../redux/actions/companyActions";
import {
  UsergroupAddOutlined,
  BorderlessTableOutlined,
} from "@ant-design/icons";
import { fetchOperatorSummaryReport } from "../../redux/actions/reportActions";
import { fetchOperatorSummaryReportPdf } from "../../redux/actions/downloadPdfAction";
import HeaderSelector from "../../components/reports/operatorSummaryReport/headerSelector";
import requireAuth from "../../hocs/requireAuth";
import { useExcelExport } from "../../hooks/useExcelExport";
import ExcelExportButton from "../../components/breakdownSetting/ExcelExportButton";
import { COMPANY_ADMIN_ROLE_ACCESS } from "../../constants";

const { Title } = Typography;

const PageBreadcrumb = () => {
  return (
    <Breadcrumb
      style={{ fontSize: "14px" }}
      items={[
        {
          title: <BorderlessTableOutlined style={{ fontSize: "16px" }} />,
        },
        {
          title: (
            <>
              <UsergroupAddOutlined style={{ fontSize: "16px" }} />
              <span>Operator</span>
            </>
          ),
        },
        {
          title: "Summary Report",
        },
      ]}
    />
  );
};

const initialState = {
  company: undefined,
  templateType: undefined,
  date: dayjs(),
};

const OperatorSummaryReport = () => {
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [operatorSummary, setOperatorSummary] = useState(undefined);
  const [showEmpty, setShowEmpty] = useState(false);
  const [filter, setFilters] = useState(initialState);
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { wrapperRef, onDownload, isReady } = useExcelExport(
    [operatorSummary, getOperatorSummaryColumns],
    `Operator-Summary-${get(filter, "date", "").format("MM-YYYY")}`,
    `Operator-Summary`
  );
  const setinitialCompany = (company) => {
    setFilters({
      ...filter,
      templateType: undefined,
      company,
    });

    setConfig({
      ...config,
      companies: [company],
      templates: get(company, "template_access_list"),
    });
    form.setFieldsValue({
      template: undefined,
      Company: get(company, "id"),
    });
  };

  const getCompanies = () => {
    setLoading(true);
    dispatch(fetchCompanies())
      .then((result) => {
        if (get(result,'length') === 1) {
          setinitialCompany(result[0]);
        } else {
          setConfig({ companies: result });
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const getSummary = () => {
    setLoading(true);
    const query = {
      type: get(filter, "templateType"),
      company_uuid: get(filter, "company.id"),
      date: get(filter, "date").format("MM-YYYY"),
    };
    dispatch(fetchOperatorSummaryReport(query))
      .then((result) => {
        if (get(result, "reports", []).length > 0) {
          setShowEmpty(false);
          setOperatorSummary(result);
        } else {
          setOperatorSummary(undefined);
          setShowEmpty(true);
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const onDownloadReport = () => {
    setLoading(true);
    const query = {
      type: get(filter, "templateType"),
      company_uuid: get(filter, "company.id"),
      date: get(filter, "date").format("MM-YYYY"),
    };
    dispatch(fetchOperatorSummaryReportPdf(query))
      .then((result) => {
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getCompanies();
  }, []);
  return (
    <div className="p-2 h-full flex flex-col">
      <div className="flex flex-row justify-between pb-4">
        <PageBreadcrumb />
      </div>

      <div className="h-full rounded-xl overflow-hidden bg-white flex-1 overflow-y-auto scrollbar-hidden pb-5 min-w-full">
        <Form form={form}>
          <Spin spinning={loading}>
            <HeaderSelector
              form={form}
              filter={filter}
              config={config}
              setConfig={setConfig}
              setFilters={setFilters}
              getSummary={getSummary}
              setOperatorSummary={setOperatorSummary}
            />
            {get(operatorSummary, "reports", []).length > 0 && (
              <div className="w-full px-8 overflow-y-auto">
                <div className="float-right mb-5 space-x-2">
                  <Tooltip title="Download Report as PDF">
                    <Button
                      type="primary"
                      icon={<DownloadOutlined />}
                      onClick={() => onDownloadReport()}
                    />
                  </Tooltip>
                  <Tooltip title="Export Operator Summary Report Excel">
                    <ExcelExportButton
                      onDownload={onDownload}
                      disabled={!isReady}
                    />
                  </Tooltip>
                </div>

                <div className="w-full table-container">
                  <Title className="mt-10" level={5}>
                    Operator Summary
                  </Title>
                  <div ref={wrapperRef}>
                    <Table
                      className="mb-20 mt-10 custom-horizontal-scrollbar"
                      dataSource={get(operatorSummary, "reports")}
                      columns={getOperatorSummaryColumns(
                        get(operatorSummary, "weeks", []),
                        get(filter, "templateType")
                      )}
                      bordered
                      pagination={false}
                     rowKey={(record) => get(record, "id") || crypto.randomUUID()}
                    />
                  </div>
                </div>
              </div>
            )}

            {showEmpty && (
              <div className="px-8 py-8">
                <Result
                  title={`No operator exists...for this month!`}
                  subTitle="Please try again with another company, template type or month"
                  status="404"
                />
              </div>
            )}
          </Spin>
        </Form>
      </div>
    </div>
  );
};

export default requireAuth(OperatorSummaryReport,COMPANY_ADMIN_ROLE_ACCESS);
